import React from 'react'
import {
  LuBuilding2,
  LuCalendar1,
  LuClock,
  LuHourglass,
  LuHouse,
  LuMapPin,
  LuUsers,
} from 'react-icons/lu'

import SchedulerSection from '@/components/integration/nylas/scheduler/components/SchedulerSection'
import InviteesCell from '@/components/integration/nylas/scheduler/components/summary/InviteesCell'
import SummaryTableRow from '@/components/integration/nylas/scheduler/components/summary/SummaryTableRow'
import { MEETING_FORMAT_DETAILS } from '@/components/integration/nylas/scheduler/constants'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import {
  formatClientHomeAddress,
  formatTimeShort,
  isClientHomeAddressComplete,
} from '@/components/integration/nylas/scheduler/helpers'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCaption } from '@/components/ui/table'
import { MEETING_FORMAT } from '@/lib/constants'

interface Props {
  onSubmit: () => void
}

const MeetingSummary: React.FC<Props> = ({ onSubmit }) => {
  const {
    timezone,
    durationMinutes,
    availableLocations,
    availableHomeVisitLocations,
    isRescheduling,
    hasHiddenClientHomeAddress,
    hiddenClientHomeTownOrCity,
  } = useSchedulerConfig()
  const {
    selectedDateTime,
    selectedFormat,
    selectedLocationId,
    clientHomeAddress,
  } = useSchedulerSelection()

  if (!selectedDateTime) {
    return null
  }

  const formatDetails = selectedFormat
    ? MEETING_FORMAT_DETAILS[selectedFormat]
    : null

  const isAtOffice = selectedFormat === MEETING_FORMAT.in_person_office
  const isAtClientHome = selectedFormat === MEETING_FORMAT.in_person_client_home

  // The office to meet at, or the office handling the visit to the client's home
  const selectedLocation = (
    isAtOffice
      ? availableLocations
      : isAtClientHome
        ? availableHomeVisitLocations
        : undefined
  )?.find((location) => location.id === selectedLocationId)

  const hasCompleteClientHomeAddress =
    isClientHomeAddressComplete(clientHomeAddress)

  function canBookMeeting() {
    if (!selectedDateTime) {
      return false
    }

    // An office meeting needs its office, and a home visit needs the address
    if (isAtOffice) {
      return !!selectedLocation
    }

    if (isAtClientHome) {
      return hasHiddenClientHomeAddress || hasCompleteClientHomeAddress
    }

    return true
  }

  return (
    <SchedulerSection autoScrollOnMount>
      <hr className="my-6" />
      <Alert>
        <AlertDescription>
          <Table className="w-full" striped={false}>
            <TableCaption className="sr-only">
              Your booking details
            </TableCaption>

            <TableBody>
              {formatDetails && (
                <SummaryTableRow
                  Icon={formatDetails.icon}
                  title="Format"
                  content={formatDetails.label}
                />
              )}

              {isAtOffice && selectedLocation && (
                <SummaryTableRow
                  Icon={LuMapPin}
                  title="Office"
                  content={
                    <>
                      <div>{selectedLocation.name}</div>

                      {selectedLocation.formattedAddress && (
                        <div className="text-muted-foreground text-xs">
                          {selectedLocation.formattedAddress}
                        </div>
                      )}
                    </>
                  }
                />
              )}

              {isAtClientHome && (
                <SummaryTableRow
                  Icon={LuHouse}
                  title="Address"
                  content={
                    hasHiddenClientHomeAddress ? (
                      hiddenClientHomeTownOrCity ? (
                        `The address on file in ${hiddenClientHomeTownOrCity}`
                      ) : (
                        "The client's address on file"
                      )
                    ) : hasCompleteClientHomeAddress ? (
                      formatClientHomeAddress(clientHomeAddress)
                    ) : (
                      <span className="text-muted-foreground">
                        Fill in the address above
                      </span>
                    )
                  }
                />
              )}

              {isAtClientHome && selectedLocation && (
                <SummaryTableRow
                  Icon={LuBuilding2}
                  title="Handling office"
                  content={selectedLocation.name}
                />
              )}

              <SummaryTableRow
                Icon={LuCalendar1}
                title="Date"
                content={selectedDateTime.toLocaleDateString('en-GB', {
                  timeZone: timezone,
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              />

              <SummaryTableRow
                Icon={LuClock}
                title="Time"
                content={formatTimeShort(selectedDateTime, timezone)}
              />

              <SummaryTableRow
                Icon={LuHourglass}
                title="Duration"
                content={`${durationMinutes} minutes`}
              />

              {/* The participants are fixed when rescheduling */}
              {!isRescheduling && (
                <SummaryTableRow
                  Icon={LuUsers}
                  title="Invitees"
                  content={<InviteesCell />}
                />
              )}
            </TableBody>
          </Table>

          <Button
            size="lg"
            disabled={!canBookMeeting()}
            className="mt-3 flex w-full items-center"
            onClick={() => onSubmit()}
          >
            <LuCalendar1 size={20} />
            {isRescheduling ? 'Reschedule' : 'Book now'}
          </Button>
        </AlertDescription>
      </Alert>
    </SchedulerSection>
  )
}

export default MeetingSummary
